import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, Printer, Download, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnifiedBillingPage() {
  const { token, user } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/billing/unified-statement?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(await r.json());
    } finally { setLoading(false); }
  }, [days, token]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    const r = await fetch(`${API}/billing/unified-statement.csv?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `statement_${days}d.csv`;
    a.click();
  };

  // Shipping is what the merchant paid for the label; our cost and margin stay on the admin side (Mike, 12-oct).
  const totals = data?.totals || { shipments: 0, shipping: 0, admin_fee: 0, insurance_fee: 0, billed_total: 0 };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="unified-billing-page">
      {/* Print header */}
      <div className="flex items-start justify-between gap-4 flex-wrap print:mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Receipt className="w-6 h-6 text-emerald-500 print:hidden" /> Unified Billing Statement
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            All carriers · last {days} days · {totals.shipments} shipments
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {user?.company_name || user?.email} · Generated {new Date().toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <div className="w-32">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger data-testid="days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[7, 14, 30, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>Last {d} days</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Totals grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat label="Shipments" value={totals.shipments} testid="stat-count" />
        <Stat label="Shipping" value={`$${(totals.shipping || 0).toFixed(2)}`} testid="stat-shipping" />
        <Stat label="Admin fees" value={`$${(totals.admin_fee || 0).toFixed(2)}`} testid="stat-admin-fee" />
        <Stat label="Coverage" value={`$${totals.insurance_fee.toFixed(2)}`} tone="text-indigo-600" testid="stat-insurance" />
        <Stat label="Billable total" value={`$${totals.billed_total.toFixed(2)}`} tone="text-amber-700" bold testid="stat-total" />
      </div>

      {/* Line items */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Line items · all carriers</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : !data?.entries?.length ? (
            <p className="text-sm text-slate-400 text-center py-8">No shipments in this window.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-2">Date</th>
                    <th className="text-left px-2 py-2">Carrier</th>
                    <th className="text-left px-2 py-2">Service</th>
                    <th className="text-left px-2 py-2">Reference</th>
                    <th className="text-right px-2 py-2">Shipping</th>
                    <th className="text-right px-2 py-2">Admin fee</th>
                    <th className="text-right px-2 py-2">Coverage</th>
                    <th className="text-right px-2 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-2 py-2">{e.timestamp ? new Date(e.timestamp).toLocaleDateString() : '—'}</td>
                      <td className="px-2 py-2">{e.carrier}</td>
                      <td className="px-2 py-2 truncate max-w-[120px]">{e.service || '—'}</td>
                      <td className="px-2 py-2 font-mono text-[10px] truncate max-w-[120px]">{e.reference}</td>
                      <td className="px-2 py-2 text-right tabular-nums">${(e.shipping || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">+${(e.admin_fee || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-indigo-600">+${(e.insurance_fee || 0).toFixed(2)}</td>
                      <td className="px-2 py-2 text-right tabular-nums font-semibold">${(e.billed_total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 dark:bg-slate-900/60 font-semibold">
                  <tr>
                    <td colSpan={4} className="px-2 py-2 text-right">Total</td>
                    <td className="px-2 py-2 text-right tabular-nums">${(totals.shipping || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">${(totals.admin_fee || 0).toFixed(2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-indigo-600">${totals.insurance_fee.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-amber-700">${totals.billed_total.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 print:mt-8">
        SelleSend Unified Statement · covers SelleGard Freight, EasyPost, and any other connected carrier.
      </p>
    </div>
  );
}

const Stat = ({ label, value, tone = '', bold = false, testid }) => (
  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-lg sm:text-2xl tabular-nums mt-1 ${bold ? 'font-bold' : 'font-semibold'} ${tone}`}>{value}</p>
  </div>
);
